import React, { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: '◈' },
    { to: '/journal', label: 'Journal', icon: '✦' },
    { to: '/letter', label: 'My Letter', icon: '✉' },
    { to: '/profile', label: 'My Profile', icon: '👤' },
    { to: '/settings', label: 'Settings', icon: '⚙' },
  ]

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--cream)',
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* ── TOP NAV ── */}
      <nav style={{
        background: 'var(--white)',
        borderBottom: '1px solid var(--cream-dark)',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '64px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: 'var(--shadow-sm)',
      }}>

        {/* Logo */}
        <div
          onClick={() => navigate('/dashboard')}
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '22px',
            fontWeight: 700,
            color: 'var(--navy)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>

        {/* Desktop Nav Links */}
        <div
          className="desktop-nav"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {navLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: 'var(--radius-full)',
                fontSize: '13px',
                fontWeight: 500,
                color: isActive ? 'var(--gold)' : 'var(--muted)',
                background: isActive ? 'rgba(200,150,62,0.1)' : 'transparent',
                textDecoration: 'none',
                transition: 'all var(--transition-fast)',
              })}
            >
              <span>{link.icon}</span>
              <span>{link.label}</span>
            </NavLink>
          ))}

          {/* User + Logout */}
          <div style={{
            marginLeft: '12px',
            paddingLeft: '12px',
            borderLeft: '1px solid var(--cream-dark)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <span style={{
              fontSize: '13px',
              color: 'var(--muted)',
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap' as const,
            }}>
              {user?.display_name || user?.email}
            </span>
            <button
              onClick={handleLogout}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                border: '1px solid var(--warm-border)',
                borderRadius: 'var(--radius-full)',
                fontSize: '13px',
                color: 'var(--muted)',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                transition: 'all var(--transition-fast)',
                whiteSpace: 'nowrap' as const,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--rose)'
                e.currentTarget.style.color = 'var(--rose)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--warm-border)'
                e.currentTarget.style.color = 'var(--muted)'
              }}
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="mobile-menu-btn"
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            flexDirection: 'column',
            gap: '5px',
          }}
        >
          <span style={{
            display: 'block', width: '24px', height: '2px',
            background: mobileMenuOpen ? 'var(--gold)' : 'var(--ink)',
            transition: 'all var(--transition-fast)',
            transform: mobileMenuOpen
              ? 'rotate(45deg) translate(5px, 5px)' : 'none',
          }} />
          <span style={{
            display: 'block', width: '24px', height: '2px',
            background: mobileMenuOpen ? 'var(--gold)' : 'var(--ink)',
            opacity: mobileMenuOpen ? 0 : 1,
            transition: 'all var(--transition-fast)',
          }} />
          <span style={{
            display: 'block', width: '24px', height: '2px',
            background: mobileMenuOpen ? 'var(--gold)' : 'var(--ink)',
            transition: 'all var(--transition-fast)',
            transform: mobileMenuOpen
              ? 'rotate(-45deg) translate(5px, -5px)' : 'none',
          }} />
        </button>
      </nav>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu"
          style={{
            background: 'var(--white)',
            borderBottom: '1px solid var(--cream-dark)',
            padding: '16px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          {navLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={() => setMobileMenuOpen(false)}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                borderRadius: 'var(--radius-md)',
                fontSize: '15px',
                fontWeight: 500,
                color: isActive ? 'var(--gold)' : 'var(--ink-soft)',
                background: isActive ? 'rgba(200,150,62,0.1)' : 'transparent',
                textDecoration: 'none',
              })}
            >
              <span>{link.icon}</span>
              <span>{link.label}</span>
            </NavLink>
          ))}

          <div style={{
            marginTop: '8px',
            paddingTop: '12px',
            borderTop: '1px solid var(--cream-dark)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              {user?.display_name || user?.email}
            </span>
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid var(--warm-border)',
                borderRadius: 'var(--radius-full)',
                fontSize: '13px',
                color: 'var(--muted)',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <main
        className="main-content"
        style={{
          flex: 1,
          maxWidth: '900px',
          width: '100%',
          margin: '0 auto',
          padding: '40px 24px',
        }}
      >
        {children}
      </main>

      {/* ── MOBILE BOTTOM NAV ── */}
      <nav
        className="mobile-bottom-nav"
        style={{
          display: 'none',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--white)',
          borderTop: '1px solid var(--cream-dark)',
          padding: '8px 0 20px',
          zIndex: 100,
          boxShadow: '0 -4px 16px rgba(28,25,23,0.08)',
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
        }}>
          {navLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              style={({ isActive }) => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                textDecoration: 'none',
                color: isActive ? 'var(--gold)' : 'var(--muted)',
              })}
            >
              <span style={{ fontSize: '18px' }}>{link.icon}</span>
              <span style={{ fontSize: '9px', fontWeight: 500 }}>
                {link.label}
              </span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .mobile-bottom-nav { display: block !important; }
          .main-content {
            padding: 24px 16px 100px !important;
          }
        }
        @media (min-width: 769px) {
          .mobile-menu { display: none !important; }
          .mobile-menu-btn { display: none !important; }
        }
      `}</style>

    </div>
  )
}